/**
 * Admin Lead Management Dashboard
 * 
 * Features:
 * - Lead list with filters (source, stage, intent, status)
 * - Lead detail view with transcript, audit log
 * - Actions: assign, contact, convert, mark lost
 * - Manual lead creation
 * - SLA breach indicators
 * - Stats overview
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import client from '../api/client';
import UnifiedAdminLayout from '../components/admin/UnifiedAdminLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '@/utils/portalNotifications';
import {
  Users,
  Plus,
  Search,
  Filter,
  Eye,
  Phone,
  Mail,
  MessageSquare,
  UserCheck,
  UserX,
  Clock,
  AlertTriangle,
  TrendingUp,
  RefreshCw,
  ChevronRight,
  Send,
  Merge,
  Bot,
  FileText,
} from 'lucide-react';

// Intent score colors
const intentColors = {
  HIGH: 'bg-green-100 text-green-800 border-green-200',
  MEDIUM: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  LOW: 'bg-gray-100 text-gray-800 border-gray-200',
};

/** Radix Select forbids `SelectItem value=""`. */
const LEAD_INTENT_SCORE_AUTO = '__lead_intent_auto__';

/** Sentinel prefix when API returns blank entries in filter lists (source / stage / intent / service). */
const LEAD_FILTER_BLANK_PREFIX = '__lead_blank:';

function leadFilterSelectValue(raw, kind, index) {
  const s = String(raw ?? '').trim();
  if (s !== '') return String(raw ?? '');
  return `${LEAD_FILTER_BLANK_PREFIX}${kind}:${index}`;
}

function decodeLeadFilterSelectValue(value) {
  if (typeof value !== 'string' || !value.startsWith(LEAD_FILTER_BLANK_PREFIX)) return value;
  return '';
}

// Stage colors
const stageColors = {
  NEW: 'bg-blue-100 text-blue-800',
  CONTACTED: 'bg-purple-100 text-purple-800',
  QUALIFIED: 'bg-indigo-100 text-indigo-800',
  NURTURING: 'bg-amber-100 text-amber-800',
  SALES_READY: 'bg-emerald-100 text-emerald-800',
  PROPOSAL_SENT: 'bg-cyan-100 text-cyan-800',
  NEGOTIATING: 'bg-slate-100 text-slate-800',
  WON: 'bg-green-100 text-green-800',
  LOST: 'bg-red-100 text-red-800',
  INACTIVE: 'bg-gray-100 text-gray-600',
};

// Source icons
const sourceIcons = {
  WEB_CHAT: MessageSquare,
  WHATSAPP: Phone,
  INTAKE_ABANDONED: Clock,
  DOCUMENT_SERVICES: FileText,
  ADMIN: Users,
  CONTACT_FORM: Mail,
};

export default function AdminLeadsPage() {
  useAuth(); // Ensure authenticated
  
  // State
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sources, setSources] = useState({
    source_platforms: [],
    service_interests: [],
    intent_scores: [],
    stages: [],
    statuses: [],
  });
  
  // Filters
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [stageFilter, setStageFilter] = useState('all');
  const [intentFilter, setIntentFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [slaBreachOnly, setSlaBreachOnly] = useState(false);
  const [lastActivityFrom, setLastActivityFrom] = useState('');
  const [lastActivityTo, setLastActivityTo] = useState('');
  const [leadScoreMin, setLeadScoreMin] = useState('');
  const [leadScoreMax, setLeadScoreMax] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [notifications, setNotifications] = useState(null);
  const [automationSummary, setAutomationSummary] = useState(null);
  const [automationEmailPerformance, setAutomationEmailPerformance] = useState(null);
  const [automationSummarySortMode, setAutomationSummarySortMode] = useState('best_conversion');
  const leadPerfHighlightMap = React.useMemo(() => {
    const map = {};
    const rows = automationEmailPerformance?.highlights;
    if (Array.isArray(rows)) {
      rows.forEach((h) => {
        if (h?.flow) map[h.flow] = h;
      });
    }
    return map;
  }, [automationEmailPerformance]);

  
  // Dialog state
  const [selectedLead, setSelectedLead] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState(null);
  
  // Form state
  const [createForm, setCreateForm] = useState({
    name: '',
    email: '',
    phone: '',
    company_name: '',
    service_interest: 'UNKNOWN',
    message_summary: '',
    intent_score: '',
    admin_notes: '',
  });
  const [actionForm, setActionForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Fetch sources (normalize to always have array fields so .map never throws)
  useEffect(() => {
    const fetchSources = async () => {
      try {
        const response = await client.get('/admin/leads/sources');
        const data = response?.data || {};
        setSources({
          source_platforms: Array.isArray(data.source_platforms) ? data.source_platforms : [],
          service_interests: Array.isArray(data.service_interests) ? data.service_interests : [],
          intent_scores: Array.isArray(data.intent_scores) ? data.intent_scores : [],
          stages: Array.isArray(data.stages) ? data.stages : [],
          statuses: Array.isArray(data.statuses) ? data.statuses : [],
        });
      } catch (error) {
        console.error('Failed to fetch sources:', error);
        setSources({
          source_platforms: [],
          service_interests: [],
          intent_scores: [],
          stages: [],
          statuses: [],
        });
      }
    };
    fetchSources();
  }, []);

  // Fetch leads
  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (sourceFilter !== 'all') {
        params.append('source_platform', decodeLeadFilterSelectValue(sourceFilter));
      }
      if (stageFilter !== 'all') {
        params.append('stage', decodeLeadFilterSelectValue(stageFilter));
      }
      if (intentFilter !== 'all') {
        params.append('intent_score', decodeLeadFilterSelectValue(intentFilter));
      }
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (slaBreachOnly) params.append('sla_breach_only', 'true');
      if (lastActivityFrom) params.append('last_activity_from', lastActivityFrom);
      if (lastActivityTo) params.append('last_activity_to', lastActivityTo);
      if (leadScoreMin !== '') params.append('lead_score_min', String(leadScoreMin));
      if (leadScoreMax !== '') params.append('lead_score_max', String(leadScoreMax));
      params.append('page', page.toString());
      params.append('limit', '50');
      
      const response = await client.get(`/admin/leads?${params.toString()}`);
      const data = response?.data || {};
      setLeads(Array.isArray(data.leads) ? data.leads : []);
      setTotal(typeof data.total === 'number' ? data.total : 0);
      setStats(data.stats && typeof data.stats === 'object' ? data.stats : {});
    } catch (error) {
      console.error('Failed to fetch leads:', error);
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [search, sourceFilter, stageFilter, intentFilter, statusFilter, slaBreachOnly, lastActivityFrom, lastActivityTo, leadScoreMin, leadScoreMax, page]);

  // Fetch lead notifications (high intent, sales ready, SLA)
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await client.get('/admin/leads/notifications');
        setNotifications(response?.data || null);
      } catch {
        setNotifications(null);
      }
    };
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchAutomationSummary = async () => {
      try {
        const [summaryResponse, emailPerfResponse] = await Promise.all([
          client.get('/admin/leads/automation/sequences/active', { params: { limit: 50 } }),
          client.get('/admin/leads/automation/email-performance', { params: { days: 30 } }),
        ]);
        setAutomationSummary(summaryResponse?.data || null);
        setAutomationEmailPerformance(emailPerfResponse?.data || null);
      } catch {
        setAutomationSummary(null);
        setAutomationEmailPerformance(null);
      }
    };
    fetchAutomationSummary();
    const interval = setInterval(fetchAutomationSummary, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  // Open lead detail
  const openLeadDetail = async (leadId) => {
    try {
      const response = await client.get(`/admin/leads/${leadId}`);
      setSelectedLead(response.data);
      setDetailDialogOpen(true);
    } catch (error) {
      console.error('Failed to load lead:', error);
      toast.error('Failed to load lead details');
    }
  };

  // Create lead
  const handleCreateLead = async () => {
    if (!createForm.email) {
      toast.error('Email is required');
      return;
    }
    
    setSaving(true);
    try {
      const params = new URLSearchParams();
      Object.entries(createForm).forEach(([key, value]) => {
        const v =
          typeof value === 'string' ? decodeLeadFilterSelectValue(value) : value;
        if (v) params.append(key, v);
      });
      
      await client.post(`/admin/leads?${params.toString()}`);
      toast.success('Lead created');
      setCreateDialogOpen(false);
      setCreateForm({
        name: '',
        email: '',
        phone: '',
        company_name: '',
        service_interest: 'UNKNOWN',
        message_summary: '',
        intent_score: '',
        admin_notes: '',
      });
      fetchLeads();
    } catch (error) {
      console.error('Failed to create lead:', error);
      toast.error(error.response?.data?.detail || 'Failed to create lead');
    } finally {
      setSaving(false);
    }
  };

  // Lead actions
  const openAction = (lead, type) => {
    setSelectedLead(lead);
    setActionType(type);
    setActionForm({});
    setActionDialogOpen(true);
  };

  const handleAction = async () => {
    if (!selectedLead || !actionType) return;
    
    setSaving(true);
    try {
      switch (actionType) {
        case 'contact':
          await client.post(`/admin/leads/${selectedLead.lead_id}/contact`, null, {
            params: {
              contact_method: actionForm.contact_method || 'email',
              notes: actionForm.notes,
              outcome: actionForm.outcome,
            }
          });
          toast.success('Contact logged');
          break;
        
        case 'assign':
          await client.post(`/admin/leads/${selectedLead.lead_id}/assign`, null, {
            params: {
              admin_id: actionForm.admin_id,
              notify_admin: actionForm.notify_admin || true,
            }
          });
          toast.success('Lead assigned');
          break;
        
        case 'convert':
          await client.post(`/admin/leads/${selectedLead.lead_id}/convert`, null, {
            params: {
              client_id: actionForm.client_id,
              conversion_notes: actionForm.notes,
              conversion_source: actionForm.conversion_source,
            }
          });
          toast.success('Lead converted to client');
          break;
        
        case 'lost':
          await client.post(`/admin/leads/${selectedLead.lead_id}/mark-lost`, null, {
            params: {
              reason: actionForm.reason,
              competitor: actionForm.competitor,
            }
          });
          toast.success('Lead marked as lost');
          break;
        
        case 'message':
          await client.post(`/admin/leads/${selectedLead.lead_id}/send-message`, null, {
            params: {
              subject: actionForm.subject,
              message: actionForm.message,
            }
          });
          toast.success('Message sent');
          break;
        
        case 'summary':
          await client.post(`/admin/leads/${selectedLead.lead_id}/generate-summary`);
          toast.success('AI summary generated');
          break;
      }
      
      setActionDialogOpen(false);
      fetchLeads();
      
      // Refresh detail if open
      if (detailDialogOpen && selectedLead) {
        openLeadDetail(selectedLead.lead_id);
      }
    } catch (error) {
      console.error('Action failed:', error);
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : (detail && typeof detail === 'object' && detail.message ? detail.message : 'Action failed');
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const SourceIcon = (source) => sourceIcons[source] || Users;

  return (
    <UnifiedAdminLayout>
    <div className="space-y-6" data-testid="admin-leads-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="h-6 w-6 text-teal-600" />
            Lead Management
          </h1>
          <p className="text-gray-500 mt-1">Capture, qualify, and convert leads</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchLeads}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button
            onClick={() => setCreateDialogOpen(true)}
            className="bg-teal-600 hover:bg-teal-700"
            data-testid="create-lead-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Lead
          </Button>
        </div>
      </div>

      {/* Lead alerts: high intent, sales ready, SLA breach */}
      {notifications && (notifications.high_intent_alerts?.length > 0 || notifications.sales_ready_alerts?.length > 0 || notifications.sla_breach_alerts?.length > 0) && (
        <div className="grid md:grid-cols-3 gap-4">
          {Array.isArray(notifications.high_intent_alerts) && notifications.high_intent_alerts.length > 0 && (
            <Card className="border-amber-200 bg-amber-50">
              <CardHeader className="py-3">
                <CardTitle className="text-sm font-medium text-amber-800 flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  High intent ({notifications.high_intent_alerts.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="py-2">
                <ul className="space-y-1 text-sm">
                  {notifications.high_intent_alerts.slice(0, 5).map((a) => (
                    <li key={a.lead_id}>
                      <button
                        type="button"
                        className="text-amber-800 hover:underline font-medium"
                        onClick={() => openLeadDetail(a.lead_id)}
                      >
                        {a.name || a.lead_id}
                      </button>
                      {a.service && <span className="text-amber-600 ml-1">· {a.service}</span>}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
          {Array.isArray(notifications.sales_ready_alerts) && notifications.sales_ready_alerts.length > 0 && (
            <Card className="border-green-200 bg-green-50">
              <CardHeader className="py-3">
                <CardTitle className="text-sm font-medium text-green-800 flex items-center gap-2">
                  <UserCheck className="h-4 w-4" />
                  Sales ready ({notifications.sales_ready_alerts.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="py-2">
                <ul className="space-y-1 text-sm">
                  {notifications.sales_ready_alerts.slice(0, 5).map((a) => (
                    <li key={a.lead_id}>
                      <button
                        type="button"
                        className="text-green-800 hover:underline font-medium"
                        onClick={() => openLeadDetail(a.lead_id)}
                      >
                        {a.name || a.lead_id}
                      </button>
                      {(a.lead_score != null || a.service) && (
                        <span className="text-green-600 ml-1">
                          · {[a.lead_score != null ? `Score ${a.lead_score}` : null, a.service].filter(Boolean).join(' ')}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
          {Array.isArray(notifications.sla_breach_alerts) && notifications.sla_breach_alerts.length > 0 && (
            <Card className="border-red-200 bg-red-50">
              <CardHeader className="py-3">
                <CardTitle className="text-sm font-medium text-red-800 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  SLA breach ({notifications.sla_breach_alerts.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="py-2">
                <ul className="space-y-1 text-sm">
                  {notifications.sla_breach_alerts.slice(0, 5).map((a) => (
                    <li key={a.lead_id}>
                      <button
                        type="button"
                        className="text-red-800 hover:underline font-medium"
                        onClick={() => openLeadDetail(a.lead_id)}
                      >
                        {a.name || a.lead_id}
                      </button>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Stats Cards */}
      {stats != null && (
        <div className="grid md:grid-cols-6 gap-4">
          <Card>
            <CardContent className="py-4">
              <div className="text-3xl font-bold text-teal-600">{stats.total_leads ?? 0}</div>
              <div className="text-sm text-gray-500">Total Leads</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4">
              <div className="text-3xl font-bold text-blue-600">{stats.new_leads ?? 0}</div>
              <div className="text-sm text-gray-500">New</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4">
              <div className="text-3xl font-bold text-purple-600">{stats.contacted_leads ?? 0}</div>
              <div className="text-sm text-gray-500">Contacted</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4">
              <div className="text-3xl font-bold text-green-600">{stats.converted_leads ?? 0}</div>
              <div className="text-sm text-gray-500">Converted</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4">
              <div className="text-3xl font-bold text-green-700">{stats.conversion_rate ?? 0}%</div>
              <div className="text-sm text-gray-500">Conversion Rate</div>
            </CardContent>
          </Card>
          <Card className={(stats.sla_breaches_today ?? 0) > 0 ? 'border-red-300 bg-red-50' : ''}>
            <CardContent className="py-4">
              <div className={`text-3xl font-bold ${(stats.sla_breaches_today ?? 0) > 0 ? 'text-red-600' : 'text-gray-600'}`}>
                {stats.sla_breaches_today ?? 0}
              </div>
              <div className="text-sm text-gray-500 flex items-center gap-1">
                {stats.sla_breaches_today > 0 && <AlertTriangle className="h-3 w-3 text-red-500" />}
                SLA Breaches
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {automationSummary && (
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-sm">Marketing Automation Sequences</CardTitle>
            <CardDescription>Active flow states and completion totals.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-3 text-sm">
              {Array.isArray(automationSummary.metrics) && automationSummary.metrics.slice(0, 6).map((m) => (
                <div key={String(m._id)} className="rounded border p-3">
                  <div className="font-medium">{String(m._id || '').replace(/_/g, ' ')}</div>
                  <div className="text-gray-600">Total: {m.total || 0}</div>
                  <div className="text-emerald-600">Active: {m.active || 0}</div>
                  <div className="text-slate-600">Completed: {m.completed || 0}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {automationEmailPerformance && (
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-sm">Automation Email Performance (30 days)</CardTitle>
            <CardDescription>Open and click rate by flow step.</CardDescription>
          </CardHeader>
          <CardContent>
            {Array.isArray(automationEmailPerformance.highlights) && automationEmailPerformance.highlights.length > 0 && (
              <>
              <div className="mb-2 flex items-center gap-2 text-xs">
                <span className="text-gray-500">Sort:</span>
                <button
                  type="button"
                  className={`px-2 py-1 rounded border ${automationSummarySortMode === 'best_conversion' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-white text-gray-600'}`}
                  onClick={() => setAutomationSummarySortMode('best_conversion')}
                >
                  Best conversion
                </button>
                <button
                  type="button"
                  className={`px-2 py-1 rounded border ${automationSummarySortMode === 'fastest_conversion' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-white text-gray-600'}`}
                  onClick={() => setAutomationSummarySortMode('fastest_conversion')}
                >
                  Fastest conversion
                </button>
              </div>
              <div className="mb-3 flex flex-wrap gap-2">
                {[...automationEmailPerformance.highlights]
                  .sort((a, b) => {
                    if (automationSummarySortMode === 'fastest_conversion') {
                      const av = a?.fastest_median_hours == null ? Number.POSITIVE_INFINITY : Number(a.fastest_median_hours);
                      const bv = b?.fastest_median_hours == null ? Number.POSITIVE_INFINITY : Number(b.fastest_median_hours);
                      return av - bv;
                    }
                    return Number(b?.top_conversion_rate_percent || 0) - Number(a?.top_conversion_rate_percent || 0);
                  })
                  .map((h, i) => (
                  <div key={`${h?.flow || 'flow'}-${i}`} className="rounded border bg-gray-50 px-2 py-1 text-xs text-gray-700">
                    <span className="font-medium">{String(h?.flow || '').replace(/_/g, ' ')}</span>
                    <span> - Best: Step {h?.top_conversion_step ?? '—'} ({h?.top_conversion_rate_percent ?? 0}%)</span>
                    <span> - Fastest: Step {h?.fastest_step ?? '—'} ({h?.fastest_median_hours ?? '—'}h)</span>
                  </div>
                ))}
              </div>
              </>
            )}
            {Array.isArray(automationEmailPerformance.rows) && automationEmailPerformance.rows.length > 0 ? (
              <div className="overflow-x-auto border rounded">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-2">Flow</th>
                      <th className="text-right p-2">Step</th>
                      <th className="text-right p-2">Sent</th>
                      <th className="text-right p-2">Open %</th>
                      <th className="text-right p-2">Click %</th>
                      <th className="text-right p-2">Conv %</th>
                      <th className="text-right p-2">Median Hrs</th>
                      <th className="text-left p-2">Winner</th>
                    </tr>
                  </thead>
                  <tbody>
                    {automationEmailPerformance.rows.slice(0, 24).map((row, i) => (
                      (() => {
                        const flow = row?._id?.flow || '';
                        const step = row?._id?.step;
                        const highlight = leadPerfHighlightMap[flow] || {};
                        const isTopConv = highlight.top_conversion_step === step;
                        const isFastest = highlight.fastest_step === step;
                        return (
                      <tr key={`${row?._id?.flow || 'flow'}-${row?._id?.step || i}`} className="border-t">
                        <td className="p-2">{String(row?._id?.flow || '').replace(/_/g, ' ')}</td>
                        <td className="p-2 text-right">{row?._id?.step ?? '—'}</td>
                        <td className="p-2 text-right">{row?.sent ?? 0}</td>
                        <td className="p-2 text-right">{row?.open_rate_percent ?? 0}%</td>
                        <td className="p-2 text-right">{row?.click_rate_percent ?? 0}%</td>
                        <td className="p-2 text-right">{row?.conversion_rate_percent ?? 0}%</td>
                        <td className="p-2 text-right">{row?.median_hours_to_conversion ?? '—'}</td>
                        <td className="p-2">
                          <div className="flex items-center gap-1">
                            {isTopConv && (
                              <span className="text-[11px] px-2 py-0.5 rounded bg-emerald-100 text-emerald-700">Best Conv</span>
                            )}
                            {isFastest && (
                              <span className="text-[11px] px-2 py-0.5 rounded bg-blue-100 text-blue-700">Fastest</span>
                            )}
                            {!isTopConv && !isFastest && <span className="text-gray-400">—</span>}
                          </div>
                        </td>
                      </tr>
                        );
                      })()
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-500">No sent automation emails in this window yet.</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex gap-4 items-center flex-wrap bg-gray-50 p-4 rounded-lg">
        <Filter className="h-5 w-5 text-gray-400" />
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search leads..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="search-leads-input"
          />
        </div>
        <Select value={sourceFilter} onValueChange={setSourceFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Source" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sources</SelectItem>
            {(Array.isArray(sources.source_platforms) ? sources.source_platforms : []).map((s, idx) => {
              const raw = String(s ?? '');
              const value = leadFilterSelectValue(s, 'sp', idx);
              const label = raw.trim() === '' ? '(empty)' : raw.replace(/_/g, ' ');
              return (
                <SelectItem key={value} value={value}>{label}</SelectItem>
              );
            })}
          </SelectContent>
        </Select>
        <Select value={stageFilter} onValueChange={setStageFilter}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Stage" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Stages</SelectItem>
            {(Array.isArray(sources.stages) ? sources.stages : []).map((s, idx) => {
              const raw = String(s ?? '');
              const value = leadFilterSelectValue(s, 'st', idx);
              const label = raw.trim() === '' ? '(empty)' : raw;
              return (
                <SelectItem key={value} value={value}>{label}</SelectItem>
              );
            })}
          </SelectContent>
        </Select>
        <Select value={intentFilter} onValueChange={setIntentFilter}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Intent" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Intent</SelectItem>
            {(Array.isArray(sources.intent_scores) ? sources.intent_scores : []).map((s, idx) => {
              const raw = String(s ?? '');
              const value = leadFilterSelectValue(s, 'int', idx);
              const label = raw.trim() === '' ? '(empty)' : raw;
              return (
                <SelectItem key={value} value={value}>{label}</SelectItem>
              );
            })}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input
            type="checkbox"
            checked={slaBreachOnly}
            onChange={(e) => setSlaBreachOnly(e.target.checked)}
            className="rounded"
          />
          SLA Breaches
        </label>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-gray-500 whitespace-nowrap">Last activity</Label>
          <Input
            type="date"
            value={lastActivityFrom}
            onChange={(e) => setLastActivityFrom(e.target.value)}
            className="w-36 h-9"
          />
          <span className="text-gray-400">–</span>
          <Input
            type="date"
            value={lastActivityTo}
            onChange={(e) => setLastActivityTo(e.target.value)}
            className="w-36 h-9"
          />
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-gray-500 whitespace-nowrap">Score</Label>
          <Input
            type="number"
            min={0}
            max={100}
            placeholder="Min"
            value={leadScoreMin}
            onChange={(e) => setLeadScoreMin(e.target.value)}
            className="w-16 h-9"
          />
          <span className="text-gray-400">–</span>
          <Input
            type="number"
            min={0}
            max={100}
            placeholder="Max"
            value={leadScoreMax}
            onChange={(e) => setLeadScoreMax(e.target.value)}
            className="w-16 h-9"
          />
        </div>
        <Button variant="outline" size="sm" onClick={fetchLeads}>
          Apply
        </Button>
      </div>

      {/* Leads List */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading leads...</div>
      ) : (Array.isArray(leads) ? leads : []).length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">No leads found</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => setCreateDialogOpen(true)}
            >
              Create your first lead
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {(Array.isArray(leads) ? leads : []).map(lead => {
            if (!lead || typeof lead !== 'object') return null;
            const Icon = SourceIcon(lead.source_platform);
            const createdAt = lead.created_at ? new Date(lead.created_at) : null;
            return (
              <Card 
                key={lead.lead_id ?? lead.email ?? Math.random()} 
                className={`hover:shadow-md transition-shadow cursor-pointer ${lead.sla_breach ? 'border-red-300 bg-red-50' : ''}`}
                onClick={() => openLeadDetail(lead.lead_id)}
                data-testid={`lead-${lead.lead_id}`}
              >
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 flex-1">
                      <div className={`p-2 rounded-lg ${lead.sla_breach ? 'bg-red-100' : 'bg-gray-100'}`}>
                        <Icon className={`h-5 w-5 ${lead.sla_breach ? 'text-red-600' : 'text-gray-600'}`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-gray-900">
                            {lead.name || lead.email || 'Unknown'}
                          </span>
                          {lead.sla_breach && (
                            <Badge variant="destructive" className="text-xs">
                              <AlertTriangle className="h-3 w-3 mr-1" />
                              SLA Breach
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <span>{lead.email ?? ''}</span>
                          {lead.phone && <span>• {lead.phone}</span>}
                          {lead.company_name && <span>• {lead.company_name}</span>}
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="secondary" className="text-xs">
                            {(lead.source_platform ?? '').replace(/_/g, ' ')}
                          </Badge>
                          <Badge className={`text-xs ${stageColors[lead.stage] || ''}`}>
                            {lead.stage ?? ''}
                          </Badge>
                          <Badge variant="outline" className={`text-xs ${intentColors[lead.intent_score] || ''}`}>
                            {(lead.intent_score ?? '')} Intent
                          </Badge>
                          {(lead.score_band || lead.lead_score != null) && (
                            <Badge
                              variant="outline"
                              className={`text-xs ${
                                lead.score_band === 'High' ? 'bg-green-50 text-green-700 border-green-200' :
                                lead.score_band === 'Medium' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                                'bg-gray-50 text-gray-600 border-gray-200'
                              }`}
                            >
                              {lead.score_band ?? '—'} ({lead.lead_score ?? '—'})
                            </Badge>
                          )}
                          {lead.service_interest && lead.service_interest !== 'UNKNOWN' && (
                            <Badge variant="outline" className="text-xs">
                              {String(lead.service_interest).replace(/_/g, ' ')}
                            </Badge>
                          )}
                          {Array.isArray(lead.tags) && lead.tags.length > 0 && lead.tags.map((t) => (
                            <Badge key={t} variant="outline" className="text-xs bg-gray-50 text-gray-600 border-gray-200">
                              {t}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right text-xs text-gray-400">
                        {createdAt ? (
                          <>
                            <div>{createdAt.toLocaleDateString()}</div>
                            <div>{createdAt.toLocaleTimeString()}</div>
                          </>
                        ) : (
                          <div>—</div>
                        )}
                      </div>
                      <ChevronRight className="h-5 w-5 text-gray-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
          
          {/* Pagination */}
          <div className="flex items-center justify-between py-4">
            <div className="text-sm text-gray-500">
              Showing {(Array.isArray(leads) ? leads : []).length} of {typeof total === 'number' ? total : 0} leads
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage(p => p - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={(Array.isArray(leads) ? leads : []).length < 50}
                onClick={() => setPage(p => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Create Lead Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Lead Manually</DialogTitle>
            <DialogDescription>
              Add a new lead to the system
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={createForm.name}
                  onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                  placeholder="John Smith"
                />
              </div>
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={createForm.email}
                  onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                  placeholder="john@example.com"
                  required
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={createForm.phone}
                  onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })}
                  placeholder="+44 7123 456789"
                />
              </div>
              <div>
                <Label htmlFor="company">Company</Label>
                <Input
                  id="company"
                  value={createForm.company_name}
                  onChange={(e) => setCreateForm({ ...createForm, company_name: e.target.value })}
                  placeholder="Acme Ltd"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Service Interest</Label>
                <Select
                  value={createForm.service_interest}
                  onValueChange={(val) => setCreateForm({ ...createForm, service_interest: val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(Array.isArray(sources.service_interests) ? sources.service_interests : []).map((s, idx) => {
                      const raw = String(s ?? '');
                      const value = leadFilterSelectValue(s, 'svc', idx);
                      const label = raw.trim() === '' ? '(empty)' : raw.replace(/_/g, ' ');
                      return (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Intent Score</Label>
                <Select
                  value={createForm.intent_score ? createForm.intent_score : LEAD_INTENT_SCORE_AUTO}
                  onValueChange={(val) =>
                    setCreateForm({
                      ...createForm,
                      intent_score: val === LEAD_INTENT_SCORE_AUTO ? '' : val,
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Auto-calculate" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={LEAD_INTENT_SCORE_AUTO}>Auto-calculate</SelectItem>
                    {(Array.isArray(sources.intent_scores) ? sources.intent_scores : []).map((s, idx) => {
                      const raw = String(s ?? '');
                      const value = leadFilterSelectValue(s, 'int', idx);
                      const label = raw.trim() === '' ? '(empty)' : raw;
                      return (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label htmlFor="message">Message/Notes</Label>
              <Textarea
                id="message"
                value={createForm.message_summary}
                onChange={(e) => setCreateForm({ ...createForm, message_summary: e.target.value })}
                placeholder="Initial enquiry details..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateLead}
              disabled={saving}
              className="bg-teal-600 hover:bg-teal-700"
            >
              {saving ? 'Creating...' : 'Create Lead'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lead Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {selectedLead && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {selectedLead.name || selectedLead.email}
                  {selectedLead.sla_breach && (
                    <Badge variant="destructive">
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      SLA Breach
                    </Badge>
                  )}
                </DialogTitle>
                <DialogDescription>
                  {selectedLead.lead_id} • Created {new Date(selectedLead.created_at).toLocaleString()}
                </DialogDescription>
              </DialogHeader>

              <Tabs defaultValue="details">
                <TabsList>
                  <TabsTrigger value="details">Details</TabsTrigger>
                  <TabsTrigger value="transcript">Transcript</TabsTrigger>
                  <TabsTrigger value="audit">Audit Log</TabsTrigger>
                  <TabsTrigger value="events">Events</TabsTrigger>
                  <TabsTrigger value="emails">Emails</TabsTrigger>
                </TabsList>

                <TabsContent value="details" className="space-y-4">
                  {/* Contact Info */}
                  <Card>
                    <CardHeader className="py-3">
                      <CardTitle className="text-sm">Contact Information</CardTitle>
                    </CardHeader>
                    <CardContent className="grid md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Email:</span>{' '}
                        <span className="font-medium">{selectedLead.email || '-'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Phone:</span>{' '}
                        <span className="font-medium">{selectedLead.phone || '-'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Company:</span>{' '}
                        <span className="font-medium">{selectedLead.company_name || '-'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Source:</span>{' '}
                        <Badge variant="secondary">{selectedLead.source_platform?.replace(/_/g, ' ')}</Badge>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Status */}
                  <Card>
                    <CardHeader className="py-3">
                      <CardTitle className="text-sm">Status & Qualification</CardTitle>
                    </CardHeader>
                    <CardContent className="grid md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Stage:</span>{' '}
                        <Badge className={stageColors[selectedLead.stage]}>{selectedLead.stage}</Badge>
                      </div>
                      <div>
                        <span className="text-gray-500">Intent:</span>{' '}
                        <Badge variant="outline" className={intentColors[selectedLead.intent_score]}>
                          {selectedLead.intent_score}
                        </Badge>
                      </div>
                      <div>
                        <span className="text-gray-500">Service:</span>{' '}
                        <span className="font-medium">{selectedLead.service_interest?.replace(/_/g, ' ')}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Lead status:</span>{' '}
                        <span className="font-medium">{selectedLead.lead_status || 'new'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Conversion source:</span>{' '}
                        <span className="font-medium">{selectedLead.conversion_source || '-'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Time to convert:</span>{' '}
                        <span className="font-medium">
                          {selectedLead.time_to_convert_seconds != null
                            ? `${Math.round(Number(selectedLead.time_to_convert_seconds) / 3600)}h`
                            : '-'}
                        </span>
                      </div>
                      {Array.isArray(selectedLead.tags) && selectedLead.tags.length > 0 && (
                        <div className="md:col-span-3">
                          <span className="text-gray-500">Tags:</span>{' '}
                          <span className="flex flex-wrap gap-1 mt-1">
                            {selectedLead.tags.map((t) => (
                              <Badge key={t} variant="secondary" className="text-xs font-normal">
                                {t}
                              </Badge>
                            ))}
                          </span>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Nurture sequence (checklist leads only) */}
                  {selectedLead.source_platform === 'COMPLIANCE_CHECKLIST' && (
                    <Card>
                      <CardHeader className="py-3">
                        <CardTitle className="text-sm">Checklist Nurture Sequence</CardTitle>
                        <CardDescription>7-day email nurture (Day 0, 2, 4, 6, 9)</CardDescription>
                      </CardHeader>
                      <CardContent className="grid md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">Nurture stage:</span>{' '}
                          <span className="font-medium">
                            {selectedLead.nurture_stage ?? 0} / 5
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">Last nurture sent:</span>{' '}
                          <span className="font-medium">
                            {selectedLead.last_nurture_sent_at
                              ? new Date(selectedLead.last_nurture_sent_at).toLocaleString()
                              : '—'}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* AI Summary */}
                  {selectedLead.ai_summary && (
                    <Card className="bg-blue-50 border-blue-200">
                      <CardHeader className="py-3">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Bot className="h-4 w-4" />
                          AI Summary
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm">{selectedLead.ai_summary}</p>
                      </CardContent>
                    </Card>
                  )}

                  {/* Message */}
                  {selectedLead.message_summary && (
                    <Card>
                      <CardHeader className="py-3">
                        <CardTitle className="text-sm">Message</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-gray-700">{selectedLead.message_summary}</p>
                      </CardContent>
                    </Card>
                  )}

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" onClick={() => openAction(selectedLead, 'contact')}>
                      <Phone className="h-4 w-4 mr-1" />
                      Log Contact
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openAction(selectedLead, 'message')}>
                      <Send className="h-4 w-4 mr-1" />
                      Send Message
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openAction(selectedLead, 'assign')}>
                      <UserCheck className="h-4 w-4 mr-1" />
                      Assign
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openAction(selectedLead, 'summary')}>
                      <Bot className="h-4 w-4 mr-1" />
                      Generate Summary
                    </Button>
                    <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => openAction(selectedLead, 'convert')}>
                      <TrendingUp className="h-4 w-4 mr-1" />
                      Convert to Client
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => openAction(selectedLead, 'lost')}>
                      <UserX className="h-4 w-4 mr-1" />
                      Mark Lost
                    </Button>
                  </div>
                </TabsContent>

                <TabsContent value="transcript">
                  {selectedLead.transcript?.length > 0 ? (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {selectedLead.transcript.map((msg, idx) => (
                        <div
                          key={idx}
                          className={`p-3 rounded-lg ${msg.sender === 'user' ? 'bg-blue-50 ml-8' : 'bg-gray-100 mr-8'}`}
                        >
                          <div className="text-xs text-gray-500 mb-1">
                            {msg.sender === 'user' ? 'Lead' : 'Bot'} • {new Date(msg.timestamp).toLocaleString()}
                          </div>
                          <p className="text-sm">{msg.message_text}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-8">No transcript available</p>
                  )}
                </TabsContent>

                <TabsContent value="audit">
                  {selectedLead.audit_log?.length > 0 ? (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {selectedLead.audit_log.map((log, idx) => (
                        <div key={idx} className="flex items-start gap-3 text-sm py-2 border-b">
                          <div className="w-32 text-xs text-gray-400 shrink-0">
                            {new Date(log.created_at).toLocaleString()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <Badge variant="outline" className="text-xs mb-1">
                              {log.event.replace(/_/g, ' ')}
                            </Badge>
                            <div className="text-gray-600">{log.actor_id} ({log.actor_type})</div>
                            {log.details && (log.event === 'FOLLOWUP_EMAIL_SENT' || log.event === 'FOLLOWUP_EMAIL_FAILED') && (
                              <div className="text-xs text-gray-500 mt-1">
                                {log.details.template_id && <>Template: {log.details.template_id}</>}
                                {log.details.step != null && <> · Step {log.details.step}</>}
                                {log.details.nurture_stage != null && <> · Nurture stage {log.details.nurture_stage}</>}
                                {log.details.error && <> · Error: {log.details.error}</>}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-8">No audit log entries</p>
                  )}
                </TabsContent>
                <TabsContent value="events">
                  {selectedLead.events?.length > 0 ? (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {selectedLead.events.map((evt, idx) => (
                        <div key={`${evt.event_key || idx}`} className="flex items-start gap-3 text-sm py-2 border-b">
                          <div className="w-40 text-xs text-gray-400 shrink-0">
                            {evt.occurred_at ? new Date(evt.occurred_at).toLocaleString() : '—'}
                          </div>
                          <div className="flex-1">
                            <div className="font-medium text-gray-800">{String(evt.event_type || '').replace(/_/g, ' ')}</div>
                            <div className="text-xs text-gray-500">{evt.source || 'system'}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-8">No lead events recorded</p>
                  )}
                </TabsContent>
                <TabsContent value="emails">
                  {selectedLead.sequence_sends?.length > 0 ? (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {selectedLead.sequence_sends.map((row, idx) => (
                        <div key={`${row.send_id || idx}`} className="p-3 rounded-md border text-sm">
                          <div className="flex items-center justify-between">
                            <span className="font-medium">{row.sequence_key} • step {row.step}</span>
                            <Badge
                              variant={
                                row.status === 'sent'
                                  ? 'secondary'
                                  : row.status === 'suppressed_existing_lifecycle'
                                    ? 'outline'
                                    : 'destructive'
                              }
                              className={
                                row.status === 'suppressed_existing_lifecycle'
                                  ? 'border-amber-300 text-amber-700 bg-amber-50'
                                  : ''
                              }
                            >
                              {row.status === 'suppressed_existing_lifecycle' ? 'suppressed (existing lifecycle)' : row.status}
                            </Badge>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {row.created_at ? new Date(row.created_at).toLocaleString() : '—'} • {row.template_id}
                          </div>
                          {row.error_message && (
                            <div className="text-xs text-red-600 mt-1">
                              {row.error_message}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-8">No sequence emails sent</p>
                  )}
                </TabsContent>
              </Tabs>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Action Dialog */}
      <Dialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {actionType === 'contact' && 'Log Contact'}
              {actionType === 'assign' && 'Assign Lead'}
              {actionType === 'convert' && 'Convert to Client'}
              {actionType === 'lost' && 'Mark as Lost'}
              {actionType === 'message' && 'Send Message'}
              {actionType === 'summary' && 'Generate AI Summary'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {actionType === 'contact' && (
              <>
                <div>
                  <Label>Contact Method</Label>
                  <Select
                    value={actionForm.contact_method || ''}
                    onValueChange={(val) => setActionForm({ ...actionForm, contact_method: val })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select method" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="phone">Phone</SelectItem>
                      <SelectItem value="whatsapp">WhatsApp</SelectItem>
                      <SelectItem value="in_person">In Person</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Outcome</Label>
                  <Select
                    value={actionForm.outcome || ''}
                    onValueChange={(val) => setActionForm({ ...actionForm, outcome: val })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select outcome" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="answered">Answered</SelectItem>
                      <SelectItem value="voicemail">Voicemail</SelectItem>
                      <SelectItem value="no_response">No Response</SelectItem>
                      <SelectItem value="callback_requested">Callback Requested</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Notes</Label>
                  <Textarea
                    value={actionForm.notes || ''}
                    onChange={(e) => setActionForm({ ...actionForm, notes: e.target.value })}
                    placeholder="Contact notes..."
                  />
                </div>
              </>
            )}

            {actionType === 'assign' && (
              <div>
                <Label>Assign to Admin (email)</Label>
                <Input
                  value={actionForm.admin_id || ''}
                  onChange={(e) => setActionForm({ ...actionForm, admin_id: e.target.value })}
                  placeholder="admin@pleerity.com"
                />
              </div>
            )}

            {actionType === 'convert' && (
              <>
                <div>
                  <Label>Client ID</Label>
                  <Input
                    value={actionForm.client_id || ''}
                    onChange={(e) => setActionForm({ ...actionForm, client_id: e.target.value })}
                    placeholder="CLT-XXXX"
                  />
                </div>
                <div>
                  <Label>Conversion Source</Label>
                  <Input
                    value={actionForm.conversion_source || ''}
                    onChange={(e) => setActionForm({ ...actionForm, conversion_source: e.target.value })}
                    placeholder="risk_check / intake / manual"
                  />
                </div>
                <div>
                  <Label>Conversion Notes</Label>
                  <Textarea
                    value={actionForm.notes || ''}
                    onChange={(e) => setActionForm({ ...actionForm, notes: e.target.value })}
                    placeholder="How was the lead converted..."
                  />
                </div>
              </>
            )}

            {actionType === 'lost' && (
              <>
                <div>
                  <Label>Reason *</Label>
                  <Textarea
                    value={actionForm.reason || ''}
                    onChange={(e) => setActionForm({ ...actionForm, reason: e.target.value })}
                    placeholder="Why was this lead lost..."
                    required
                  />
                </div>
                <div>
                  <Label>Competitor (if applicable)</Label>
                  <Input
                    value={actionForm.competitor || ''}
                    onChange={(e) => setActionForm({ ...actionForm, competitor: e.target.value })}
                    placeholder="Competitor name"
                  />
                </div>
              </>
            )}

            {actionType === 'message' && (
              <>
                <div>
                  <Label>Subject *</Label>
                  <Input
                    value={actionForm.subject || ''}
                    onChange={(e) => setActionForm({ ...actionForm, subject: e.target.value })}
                    placeholder="Email subject"
                    required
                  />
                </div>
                <div>
                  <Label>Message *</Label>
                  <Textarea
                    value={actionForm.message || ''}
                    onChange={(e) => setActionForm({ ...actionForm, message: e.target.value })}
                    placeholder="Your message..."
                    rows={5}
                    required
                  />
                </div>
              </>
            )}

            {actionType === 'summary' && (
              <p className="text-sm text-gray-600">
                Generate an AI-powered summary of this lead based on their conversation and enquiry details.
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAction}
              disabled={saving}
              className="bg-teal-600 hover:bg-teal-700"
            >
              {saving ? 'Processing...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </UnifiedAdminLayout>
  );
}
